import { apiClient } from '@/shared/lib/axios';
import type {
  Site,
  Panel,
  TransferPanelParams,
  TransferPanelResult,
  PanelSiteHistoryResult,
  SitePanelHistoryResult,
} from '../types/site.types';

/** CSRFトークンレスポンス */
interface CsrfTokenResponse {
  readonly csrfToken: string;
}

/** 現場一覧APIレスポンス */
interface SitesApiResponse {
  readonly success: boolean;
  readonly sites?: readonly Site[];
  readonly error?: string;
}

/** 現場単体APIレスポンス */
interface SiteApiResponse {
  readonly success: boolean;
  readonly site?: Site;
  readonly error?: string;
  readonly message?: string;
}

/** 盤一覧APIレスポンス */
interface PanelsApiResponse {
  readonly success: boolean;
  readonly panels?: readonly Panel[];
  readonly error?: string;
}

/** 盤単体APIレスポンス */
interface PanelApiResponse {
  readonly success: boolean;
  readonly panel?: Panel;
  readonly error?: string;
  readonly message?: string;
}

/** 削除APIレスポンス */
interface DeleteApiResponse {
  readonly success: boolean;
  readonly error?: string;
  readonly message?: string;
}

/**
 * CSRFトークンを取得
 */
const getCsrfToken = async (): Promise<string> => {
  const response = await apiClient.get<CsrfTokenResponse>('/api/auth/csrf-token');
  return response.data.csrfToken;
};

/**
 * 現場・盤管理APIサービス
 */
export const siteApiService = {
  // ========== 現場 ==========

  /**
   * 現場一覧を取得
   */
  async getAllSites(): Promise<readonly Site[]> {
    const response = await apiClient.get<SitesApiResponse>('/api/sites');

    if (response.data.success === false || response.data.sites === undefined) {
      throw new Error(response.data.error ?? '現場一覧の取得に失敗しました');
    }

    return response.data.sites;
  },

  /**
   * 現場を作成
   */
  async createSite(params: { readonly siteName: string }): Promise<Site> {
    const csrfToken = await getCsrfToken();
    const response = await apiClient.post<SiteApiResponse>(
      '/api/sites',
      { siteName: params.siteName },
      {
        headers: {
          'x-csrf-token': csrfToken,
        },
      }
    );

    if (response.data.success === false || response.data.site === undefined) {
      throw new Error(response.data.error ?? '現場の作成に失敗しました');
    }

    return response.data.site;
  },

  /**
   * 現場を更新
   */
  async updateSite(params: { readonly siteId: number; readonly siteName: string }): Promise<Site> {
    const csrfToken = await getCsrfToken();
    const response = await apiClient.put<SiteApiResponse>(
      `/api/sites/${params.siteId}`,
      { siteName: params.siteName },
      {
        headers: {
          'x-csrf-token': csrfToken,
        },
      }
    );

    if (response.data.success === false || response.data.site === undefined) {
      throw new Error(response.data.error ?? '現場の更新に失敗しました');
    }

    return response.data.site;
  },

  /**
   * 現場を削除
   */
  async deleteSite(params: { readonly siteId: number }): Promise<void> {
    const csrfToken = await getCsrfToken();
    const response = await apiClient.delete<DeleteApiResponse>(
      `/api/sites/${params.siteId}`,
      {
        headers: {
          'x-csrf-token': csrfToken,
        },
      }
    );

    if (response.data.success === false) {
      throw new Error(response.data.error ?? '現場の削除に失敗しました');
    }
  },

  // ========== 盤 ==========

  /**
   * 盤一覧を取得
   */
  async getAllPanels(): Promise<readonly Panel[]> {
    const response = await apiClient.get<PanelsApiResponse>('/api/panels');

    if (response.data.success === false || response.data.panels === undefined) {
      throw new Error(response.data.error ?? '盤一覧の取得に失敗しました');
    }

    return response.data.panels;
  },

  /**
   * 盤を作成
   */
  async createPanel(params: {
    readonly panelName: string;
    readonly siteId: number;
  }): Promise<Panel> {
    const csrfToken = await getCsrfToken();
    const response = await apiClient.post<PanelApiResponse>(
      '/api/panels',
      {
        panelName: params.panelName,
        siteId: params.siteId,
      },
      {
        headers: {
          'x-csrf-token': csrfToken,
        },
      }
    );

    if (response.data.success === false || response.data.panel === undefined) {
      throw new Error(response.data.error ?? '盤の作成に失敗しました');
    }

    return response.data.panel;
  },

  /**
   * 盤情報を更新（盤名・デバイス識別情報）
   */
  async updatePanel(params: {
    readonly panelId: number;
    readonly panelName: string;
  }): Promise<void> {
    const csrfToken = await getCsrfToken();
    const response = await apiClient.put<PanelApiResponse>(
      `/api/panels/${params.panelId}`,
      {
        panelName: params.panelName,
      },
      {
        headers: {
          'x-csrf-token': csrfToken,
        },
      }
    );

    if (response.data.success === false) {
      throw new Error(response.data.error ?? '盤の更新に失敗しました');
    }
  },

  /**
   * 盤を削除
   */
  async deletePanel(params: { readonly panelId: number }): Promise<void> {
    const csrfToken = await getCsrfToken();
    const response = await apiClient.delete<DeleteApiResponse>(
      `/api/panels/${params.panelId}`,
      {
        headers: {
          'x-csrf-token': csrfToken,
        },
      }
    );

    if (response.data.success === false) {
      throw new Error(response.data.error ?? '盤の削除に失敗しました');
    }
  },

  // ========== 盤移動 ==========

  /**
   * 盤を別の現場に移動
   */
  async transferPanel(params: TransferPanelParams): Promise<TransferPanelResult> {
    const csrfToken = await getCsrfToken();
    const response = await apiClient.post<TransferPanelResult>(
      `/api/panels/${params.panelId}/transfer`,
      {
        targetSiteId: params.targetSiteId,
        transferReason: params.transferReason,
        note: params.note,
      },
      {
        headers: {
          'x-csrf-token': csrfToken,
        },
      }
    );

    if (response.data.success === false) {
      throw new Error('盤の移動に失敗しました');
    }

    return response.data;
  },

  // ========== 盤履歴 ==========

  /**
   * 盤の現場履歴を取得
   */
  async getPanelSiteHistory(params: { readonly panelId: number }): Promise<PanelSiteHistoryResult> {
    const response = await apiClient.get<PanelSiteHistoryResult>(
      `/api/panels/${params.panelId}/site-history`
    );

    if (response.data.success === false) {
      throw new Error('盤の履歴取得に失敗しました');
    }

    return response.data;
  },

  /**
   * 現場の盤履歴を取得
   */
  async getSitePanelHistory(params: { readonly siteId: number }): Promise<SitePanelHistoryResult> {
    const response = await apiClient.get<SitePanelHistoryResult>(
      `/api/sites/${params.siteId}/panel-history`
    );

    if (response.data.success === false) {
      throw new Error('現場の盤履歴取得に失敗しました');
    }

    return response.data;
  },
};
